import { isChatChannel } from "odyssey-shared";
import type { ChatEvent, ChatFeed } from "./events.ts";
import type { NameMatch } from "./lookup.ts";
import type { ChannelState } from "./store.ts";

/**
 * The chat alert (ADR-085's amendment "the chat alert", the author's ruling of 2026-10-03: "chat is on both"): the
 * page sounds `sfx/ui/chat/alert` when a whisper arrives for the player and when a line another player wrote, in any
 * scope the page shows, mentions the player's character name or family name. The page's own lines, its whisper coming
 * back, a family on the block list and a channel the character left never alert; the page's lines of information and
 * death lines are no chat frames and never reach it. The sound is throttled by one knob,
 * `CHAT_ALERT_INTERVAL_MS`. Each alert is of a kind, a whisper or a mention, since only a whisper's passes the
 * background mute (`alertPassesMute`, the amendment of 2026-10-13). Everything here reads the chat frames the page already receives and the names it already
 * knows: nothing is sent and the protocol is unchanged.
 */

/**
 * The shortest time between two alerts that sound, whispers and mentions together: an alert inside it is dropped, not
 * held for later.
 */
export const CHAT_ALERT_INTERVAL_MS = 3000;

/**
 * What an alert reads beside the frame: the player's own names (null before the welcome), the account's block list,
 * and the character's channels as the chat holds them (null until the gateway has sent them), of which it reads the
 * ones joined.
 */
export interface AlertContext {
  readonly self: NameMatch | null;
  readonly blocks: readonly string[];
  readonly channels: ChannelState | null;
}

/**
 * A character that joins a name to what is beside it: a letter, a digit or a combining mark of any script, or an
 * underscore. A mark joins because it belongs to the letter it follows: a name with an accent laid over its last
 * letter is another word, and lower-casing a dotted capital I leaves a mark behind its i.
 */
const JOINS = /[\p{L}\p{N}\p{M}_]/u;

/**
 * The first UTF-16 unit of the trailing half of a surrogate pair, and the last.
 */
const LOW_SURROGATE_FIRST = 0xdc00;
const LOW_SURROGATE_LAST = 0xdfff;

/**
 * Whether the character that starts at an index of a line joins a name to what is beside it, read as a whole code
 * point, so a letter outside the basic plane is a letter and not two halves that are none.
 *
 * @param text The line.
 * @param at The index the character starts at; outside the line nothing joins.
 * @returns True for a letter, a digit, a combining mark or an underscore.
 */
function joinsFrom(text: string, at: number): boolean {
  const point = at >= 0 && at < text.length ? text.codePointAt(at) : undefined;
  return point !== undefined && JOINS.test(String.fromCodePoint(point));
}

/**
 * Whether the character that ends just before an index of a line joins a name to what is beside it, read as a whole
 * code point: when the unit before the index is the trailing half of a pair, the character starts one unit earlier.
 *
 * @param text The line.
 * @param end The index after the character's last unit; before the line's start nothing joins.
 * @returns True for a letter, a digit, a combining mark or an underscore.
 */
function joinsBefore(text: string, end: number): boolean {
  const last = end - 1;
  if (last < 0) {
    return false;
  }
  const unit = text.charCodeAt(last);
  return joinsFrom(text, unit >= LOW_SURROGATE_FIRST && unit <= LOW_SURROGATE_LAST && last > 0 ? last - 1 : last);
}

/**
 * Whether a name found in a line stands whole there: neither side continues it. A side continues it with a letter, a
 * digit, a combining mark or an underscore, or with a hyphen that has one of those beyond it, since a name may hold a
 * hyphen ("Ann-Marie" is not "Ann"). An "@" directly before the name is read as part of the mention, whatever
 * precedes it.
 *
 * @param text The line, in lower case.
 * @param start The index of the name's first character.
 * @param end The index after its last.
 * @returns True when the name is a whole word there.
 */
function standsWhole(text: string, start: number, end: number): boolean {
  if (joinsFrom(text, end) || (text.charAt(end) === "-" && joinsFrom(text, end + 1))) {
    return false;
  }
  if (text.charAt(start - 1) === "@") {
    return true;
  }
  return !joinsBefore(text, start) && !(text.charAt(start - 1) === "-" && joinsBefore(text, start - 1));
}

/**
 * Whether a line mentions a name: the name as a whole word, ignoring case, with or without an "@" before it.
 * Punctuation and white space end a word, so "Kaelen," "(Kaelen)" and "Kaelen's" mention Kaelen, while "Kaelens",
 * "McKaelen", "Kaelen2", "Kaelen_alt" and "Kaelen-Marie" do not.
 *
 * @param text The line.
 * @param name One part of a name, as the page holds it.
 * @returns True when the line holds the name whole; false for an empty name.
 * @example
 * mentionsName("thanks @kaelen!", "Kaelen");
 * // true
 */
export function mentionsName(text: string, name: string): boolean {
  const wanted = name.toLowerCase();
  if (wanted === "") {
    return false;
  }
  const line = text.toLowerCase();
  for (let at = line.indexOf(wanted); at !== -1; at = line.indexOf(wanted, at + 1)) {
    if (standsWhole(line, at, at + wanted.length)) {
      return true;
    }
  }
  return false;
}

/**
 * Whether a line mentions a player: its character name or its family name, each by `mentionsName`, so the full name
 * mentions it too.
 *
 * @param text The line.
 * @param player The player's two names.
 * @returns True when either name is mentioned.
 */
export function mentionsPlayer(text: string, player: NameMatch): boolean {
  return mentionsName(text, player.firstName) || mentionsName(text, player.familyName);
}

/**
 * Whether two names are one, ignoring case.
 *
 * @param a One name.
 * @param b The other.
 * @returns True when they match.
 */
function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Whether a line another player wrote alerts: its family is not blocked, it is not the player's own, and it mentions
 * the player.
 *
 * @param speaker The speaker's names and the line.
 * @param context The player's names and the block list.
 * @returns True when the line alerts.
 */
function mentionAlerts(speaker: { readonly firstName: string; readonly familyName: string; readonly text: string }, context: AlertContext): boolean {
  const self = context.self;
  if (self === null || context.blocks.some((family) => sameName(family, speaker.familyName))) {
    return false;
  }
  if (sameName(speaker.firstName, self.firstName) && sameName(speaker.familyName, self.familyName)) {
    return false;
  }
  return mentionsPlayer(speaker.text, self);
}

/**
 * Whether a chat frame alerts the player. A whisper received does, whatever it says, unless its sender's family is
 * blocked; the page's own whisper coming back does not. A Local line, an action or a channel's line does when another
 * player wrote it and it mentions the player. A channel's line needs the channel to be one the page knows and the
 * character is in. A mute of the account limits what it may say, not what it hears, so a muted channel alerts as any
 * other. No other frame alerts.
 *
 * @param event The frame the connection read.
 * @param context The player's names, the block list and the channels.
 * @returns True when the frame alerts.
 */
export function alertsOn(event: ChatEvent, context: AlertContext): boolean {
  switch (event.kind) {
    case "whispered":
      return !event.sent && !context.blocks.some((family) => sameName(family, event.familyName));
    case "said":
    case "posed":
      return mentionAlerts(event, context);
    case "channelSaid": {
      const channel = event.channel;
      const held = context.channels;
      if (held === null || !isChatChannel(channel) || !held.joined.includes(channel)) {
        return false;
      }
      return mentionAlerts(event, context);
    }
    default:
      return false;
  }
}

/**
 * What an alert is for: a whisper received, or a line that mentions the player.
 */
export type AlertKind = "whisper" | "mention";

/**
 * Whether an alert sounds while the window is in the background with "Mute when the window is in the background"
 * on (ADR-085's amendment of 2026-10-13): a whisper's does while the viewer's switch is on; a mention's never does.
 *
 * @param kind The alert's kind.
 * @param whispersThrough The viewer's switch, "Whispers alert in the background".
 * @returns True when the alert passes the mute.
 * @example
 * alertPassesMute("whisper", true);
 * // true
 */
export function alertPassesMute(kind: AlertKind, whispersThrough: boolean): boolean {
  return kind === "whisper" && whispersThrough;
}

/**
 * What the alerts are made from: the clock, what to read beside each frame, and the sound, which is told the
 * alert's kind and answers false when it did not sound (anything else counts as sounded).
 */
export interface ChatAlertsOptions {
  readonly now: () => number;
  readonly context: () => AlertContext;
  readonly play: (kind: AlertKind) => boolean | void;
}

/**
 * The page's chat alerts: told each chat frame, they sound for the ones that alert, at most once per
 * `CHAT_ALERT_INTERVAL_MS`.
 *
 * @example
 * ```ts
 * const alerts = new ChatAlerts({ now: Date.now, context: () => ({ self: gameReport.name, blocks: chat.getSnapshot().blocks, channels: chat.getSnapshot().channels }), play: () => pageSound.ui(InterfaceSound.ChatAlert) });
 * alerts.heard({ kind: "whispered", sent: false, firstName: "Mira", familyName: "Thornwood", text: "meet me by the well" });
 * ```
 */
export class ChatAlerts {
  private soundedAt: number | null = null;

  /**
   * @param options The clock, the context and the sound.
   */
  public constructor(private readonly options: ChatAlertsOptions) {}

  /**
   * Takes a chat frame and sounds the alert when it alerts and the interval since the last alert that sounded has
   * passed. A clock that stepped back behind that alert sounds. An alert the sound answers false for did not
   * sound and starts no interval, so a mention muted in the background does not swallow a whisper after it.
   *
   * @param event The frame.
   * @returns True when the alert sounded.
   */
  public heard(event: ChatEvent): boolean {
    if (!alertsOn(event, this.options.context())) {
      return false;
    }
    const now = this.options.now();
    const last = this.soundedAt;
    if (last !== null && now >= last && now - last < CHAT_ALERT_INTERVAL_MS) {
      return false;
    }
    if (this.options.play(event.kind === "whispered" ? "whisper" : "mention") === false) {
      return false;
    }
    this.soundedAt = now;
    return true;
  }
}

/**
 * A chat feed that alerts: every frame goes to the chat first, so the log shows the line, and then to the alerts.
 * Binding, the page's lines of information and death lines pass straight to the chat and never alert.
 *
 * @param feed The chat, as the scene tells it.
 * @param alerts The alerts.
 * @returns The feed to hand the scene in the chat's place.
 */
export function alertingFeed(feed: ChatFeed, alerts: ChatAlerts): ChatFeed {
  return {
    bind: (send) => feed.bind(send),
    receive: (event) => {
      feed.receive(event);
      alerts.heard(event);
    },
    note: (text) => feed.note(text),
    death: (text) => feed.death(text),
  };
}
